import React from 'react';

import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { VALIDATION_TYPE_TRANSLATION_MAP } from 'src/features/validationTypes/utils/constants';
import { ValidationTypeFormSchemaValues } from 'src/features/validationTypes/utils/formSchema';
import { validationTypesMessages } from 'src/features/validationTypes/utils/messages';
import { VALIDATION_TYPE } from 'src/features/validationTypes/utils/types';
import { useListValidatorAccounts } from 'src/features/validatorAccounts/api/useListValidatorAccounts';
import { validatorAccountsMessages } from 'src/features/validatorAccounts/utils/messages';
import InputFormField from 'src/registry/components/forms/InputFormField';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { generalMessages } from 'src/registry/utils/messages';
import { commonMessages } from 'src/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import { CURRENCY } from 'src/types';
import { membershipMessages } from 'src/features/membership/utils/messages';
import { CURRENCY_MAP } from 'src/features/pricingPlan/utils/constants';
import { BOOLEAN_MAP, BOOLEAN_OPTIONS } from 'src/utils/constants';

type ValidationTypeFormProps = {
    form: UseFormReturn<ValidationTypeFormSchemaValues>;
    onSubmit: (values: ValidationTypeFormSchemaValues) => void;
};

function ValidationTypeForm({ onSubmit, form }: ValidationTypeFormProps) {
    const { formatMessage } = useIntl();
    const { accountId } = useParams();
    const { parkingId } = useParking();

    const { data: validatorAccounts } = useListValidatorAccounts({ parkingId });

    const [displayAmount, setDisplayAmount] = React.useState('');

    const { type, amount } = form.watch();

    const isTypeCents = type === VALIDATION_TYPE.CHARGE;
    const isTypeMinutes = type === VALIDATION_TYPE.TIME || type === VALIDATION_TYPE.WAIVE;

    React.useEffect(() => {
        if (amount == null) {
            setDisplayAmount('');
            return;
        }

        if (isTypeCents) {
            setDisplayAmount(String(amount / 100));
        } else if (isTypeMinutes) {
            setDisplayAmount(String(amount));
        } else if (type === VALIDATION_TYPE.GUEST) {
            setDisplayAmount('0');
        }
    }, [isTypeCents, amount, setDisplayAmount, isTypeMinutes, type]);

    React.useEffect(() => {
        form.setValue('can_be_stacked', undefined);
        form.setValue('can_be_combined', undefined);
    }, [form, type]);

    React.useEffect(() => {
        if (form.formState.isDirty) {
            form.setValue('amount', undefined);
        }
    }, [form, type]);

    const amountFormFieldTitle = React.useMemo(() => {
        if (!type) return validationTypesMessages.validationTypeAmount;
        switch (type) {
            case VALIDATION_TYPE.WAIVE:
                return validationTypesMessages.timeToLeave;
            case VALIDATION_TYPE.TIME:
                return validationTypesMessages.validationTypeMinutes;
            case VALIDATION_TYPE.CHARGE:
                return validationTypesMessages.validationTypeAmount;
        }
    }, [type]);

    const amountFormFieldPlaceholder =
        type === VALIDATION_TYPE.CHARGE
            ? validationTypesMessages.validationTypeAmount
            : validationTypesMessages.validationTypeMinutes;

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <InputFormField<ValidationTypeFormSchemaValues>
                form={form}
                name="name"
                label={formatMessage(generalMessages.name)}
            />
            <FormField
                control={form.control}
                name="validator_account"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...validatorAccountsMessages.validationAccount} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select onValueChange={field.onChange} value={field.value ?? ''} disabled={!!accountId}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue
                                            placeholder={`${formatMessage(validatorAccountsMessages.validationAccount)}...`}
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {validatorAccounts?.results?.map((option) => (
                                            <SelectItem key={option.id} value={option.id}>
                                                {option.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <SelectFormField<ValidationTypeFormSchemaValues>
                form={form}
                required
                name="type"
                label={formatMessage(validationTypesMessages.type)}
                options={Object.values(VALIDATION_TYPE)}
                optionTranslationMap={VALIDATION_TYPE_TRANSLATION_MAP}
            />

            {type && type !== VALIDATION_TYPE.GUEST && (
                <FormField
                    control={form.control}
                    name="amount"
                    render={({ field }) => (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog" required>
                                <FormattedMessage {...amountFormFieldTitle} />
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <Input
                                        type="text"
                                        inputMode={isTypeCents ? 'decimal' : 'numeric'}
                                        placeholder={`${formatMessage(amountFormFieldPlaceholder)}...`}
                                        value={displayAmount}
                                        onBlur={() => {
                                            if (!displayAmount) return;

                                            const numeric = parseFloat(displayAmount);

                                            if (isNaN(numeric)) return;

                                            if (isTypeCents) {
                                                setDisplayAmount(numeric.toFixed(2));
                                            } else if (isTypeMinutes) {
                                                setDisplayAmount(displayAmount);
                                            }
                                        }}
                                        onChange={(e) => {
                                            const inputValue = e.target.value;

                                            if (isTypeCents) {
                                                if (!/^\d*\.?\d{0,2}$/.test(inputValue)) return;
                                            } else if (isTypeMinutes) {
                                                if (!/^\d*$/.test(inputValue)) return;
                                            }

                                            setDisplayAmount(inputValue);

                                            const numeric = parseFloat(inputValue);

                                            if (isNaN(numeric)) {
                                                field.onChange(undefined);
                                                return;
                                            }

                                            if (isTypeCents) {
                                                field.onChange(Math.round(numeric * 100));
                                            } else if (isTypeMinutes) {
                                                field.onChange(Math.floor(numeric));
                                            }
                                        }}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    )}
                />
            )}
            {isTypeCents && (
                <SelectFormField<ValidationTypeFormSchemaValues>
                    form={form}
                    name="currency"
                    label={formatMessage(commonMessages.currency)}
                    options={Object.values(CURRENCY)}
                    optionTranslationMap={CURRENCY_MAP}
                    required={type === VALIDATION_TYPE.CHARGE}
                />
            )}
            <SelectFormField<ValidationTypeFormSchemaValues>
                form={form}
                name="can_be_stacked"
                isBoolean
                required={false}
                label={formatMessage(validationTypesMessages.canBeStacked)}
                options={BOOLEAN_OPTIONS}
                optionTranslationMap={BOOLEAN_MAP}
                disabled={type === VALIDATION_TYPE.GUEST}
            />
            <SelectFormField<ValidationTypeFormSchemaValues>
                form={form}
                name="can_be_combined"
                isBoolean
                required={false}
                label={formatMessage(validationTypesMessages.canBeCombined)}
                options={BOOLEAN_OPTIONS}
                optionTranslationMap={BOOLEAN_MAP}
                disabled={type === VALIDATION_TYPE.GUEST}
            />
            <SelectFormField<ValidationTypeFormSchemaValues>
                form={form}
                name="is_active"
                isBoolean
                required={false}
                label={formatMessage(membershipMessages.isActive)}
                options={BOOLEAN_OPTIONS}
                optionTranslationMap={BOOLEAN_MAP}
            />
        </Form>
    );
}

export default ValidationTypeForm;
